"use client"

import { useEffect, useRef } from "react"
import styles from "./hero-background.module.css"

const startPaths = [
  "M-48 96H128Q192 96 192 160V288Q192 352 256 352H552",
  "M-48 192H32Q96 192 96 256V416Q96 480 160 480H440",
  "M-48 704H224Q288 704 288 640V608Q288 544 352 544H552",
]
const endPaths = [
  "M568 116H392Q328 116 328 180V276Q328 340 264 340H-32",
  "M568 212H488Q424 212 424 276V404Q424 468 360 468H80",
  "M568 724H296Q232 724 232 660V628Q232 564 168 564H-32",
]

function relocateTrack(path: SVGPathElement) {
  const fromStart = path.closest("svg")?.dataset.side === "start"
  const horizontal = fromStart ? 1 : -1
  const vertical = Math.random() < 0.5 ? 1 : -1
  const x = Math.round(120 + Math.random() * 180)
  const cornerX = fromStart ? x : 520 - x
  const y = Math.round(220 + Math.random() * 360)
  const endY = y + vertical * Math.round(96 + Math.random() * 84)
  const radius = 32
  path.setAttribute("d", `M${fromStart ? -48 : 568} ${y}H${cornerX - horizontal * radius}Q${cornerX} ${y} ${cornerX} ${y + vertical * radius}V${endY - vertical * radius}Q${cornerX} ${endY} ${cornerX + horizontal * radius} ${endY}H${fromStart ? 552 : -32}`)
  path.style.setProperty("--track-opacity", String(0.55 + Math.random() * 0.45))
}

export function HeroBackground({ animated = false }: { animated?: boolean }) {
  const backgroundRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const background = backgroundRef.current
    if (!animated || !background) return
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)")
    const paths = Array.from(background.querySelectorAll<SVGPathElement>("[data-loading-track]"))
    if (!preference.matches) {
      paths.forEach((path, index) => {
        relocateTrack(path)
        path.style.setProperty("--track-duration", `${Math.round(1700 + Math.random() * 900)}ms`)
        path.style.setProperty("--track-delay", `${Math.round(index * 70 + Math.random() * 240)}ms`)
      })
    }
    // Each cycle ends invisibly, so geometry can change without a visible jump.
    const onIteration = (event: AnimationEvent) => {
      if (!preference.matches && event.target instanceof SVGPathElement && event.target.hasAttribute("data-loading-track")) {
        relocateTrack(event.target)
      }
    }
    background.addEventListener("animationiteration", onIteration)
    return () => background.removeEventListener("animationiteration", onIteration)
  }, [animated])

  return (
    <div ref={backgroundRef} aria-hidden="true" className={`${styles.background}${animated ? ` ${styles.animated}` : ""}`}>
      <svg className={styles.start} data-side="start" viewBox="0 0 520 820" fill="none" focusable="false">
        <g className={styles.tracks} stroke="currentColor" strokeWidth="32" strokeLinecap="round" strokeLinejoin="round">
          {startPaths.map((d) => <path key={d} d={d} pathLength={1} data-loading-track={animated ? "" : undefined} />)}
        </g>
      </svg>
      <svg className={styles.end} data-side="end" viewBox="0 0 520 820" fill="none" focusable="false">
        <g className={styles.tracks} stroke="currentColor" strokeWidth="32" strokeLinecap="round" strokeLinejoin="round">
          {endPaths.map((d) => <path key={d} d={d} pathLength={1} data-loading-track={animated ? "" : undefined} />)}
        </g>
      </svg>
      <div className={styles.veil} />
    </div>
  )
}
